import axios from "axios";
import { getAxiosConfig } from "./axiosConfig";
import { getPrestadores, getEspecialidades } from "./catalogos";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
dayjs.extend(weekOfYear);

/**
 * Obtiene los turnos disponibles de un prestador.
 * @param token Token de autenticación.
 * @param prestador Código del prestador.
 * @param urlAxioPP URL base de la API.
 * @returns Turnos disponibles y turnos dados por mutual.
 */
export const getTurnos = async (
  token: string,
  prestador: string,
  urlAxioPP: string,
) => {
  const turnosDisponibles: any[] = [];
  const turnosDadosMutual: any[] = [];
  const turnosPorSemana: any = {
    domingo: 0,
    lunes: 0,
    martes: 0,
    miercoles: 0,
    jueves: 0,
    viernes: 0,
    sabado: 0,
  };
  let turnos: any[] = [];
  try {
    const configAxios = getAxiosConfig(token, urlAxioPP);
    const response = await axios.get(
      `Turnos/TurnosPrestadorFecha/?prestador=${prestador}&desde=${dayjs().format(
        "YYYY/MM/DD hh:mm",
      )}&hasta=${dayjs().add(3, "month").format("YYYY/MM/DD hh:mm")}`,
      configAxios,
    );
    if (response.data.length !== 0) turnos = response.data;
    const diasSemana = [
      "domingo",
      "lunes",
      "martes",
      "miercoles",
      "jueves",
      "viernes",
      "sabado",
    ];
    turnos.forEach((turno) => {
      const diaSemana = dayjs(turno.fecha).get("d");
      const dia = diasSemana[diaSemana];
      let semanaTurno = dayjs(turno.fecha).week();
      if (
        turno.libre &&
        !turno.cancelado &&
        !turno.entreTurno &&
        !turno.reservado
      ) {
        turnosDisponibles.push(turno);
      }
      let turnosSemana = turnosDadosMutual.find(
        (turnoM) => turnoM.semana === semanaTurno,
      );
      if (!turnosSemana) {
        turnosPorSemana[dia] += 1;
        turnosDadosMutual.push({
          semana: semanaTurno,
          ...turnosPorSemana,
          totalTurnosAsignadosMutual: 1,
        });
      } else {
        if (turno.mutual === turno.mutual) {
          turnosSemana = {
            ...turnosSemana,
            [dia]: turnosSemana[dia] + 1,
            totalTurnosAsignadosMutual:
              turnosSemana.totalTurnosAsignadosMutual + 1,
          };
          turnosDadosMutual[
            turnosDadosMutual.findIndex((turno) => turno.semana === semanaTurno)
          ] = turnosSemana;
        }
      }
    });
    return { turnosDadosMutual, turnosDisponibles };
  } catch (error: any) {
    throw new Error(
      "Error al obtener los turnos disponibles: " + error.message,
    );
  }
};

/**
 * Actualiza (reserva) un turno para un paciente.
 * @param turnoSeleccionado Objeto turno seleccionado.
 * @param usuario Objeto usuario (paciente).
 * @param prestadorSeleccionado String con formato "prestador-mutual".
 * @param urlAxioPP URL base de la API.
 * @returns Respuesta de la API.
 */
export const actualizarTurno = async (
  turnoSeleccionado: any,
  usuario: any,
  prestadorSeleccionado: string,
  urlAxioPP: string,
) => {
  let [prestadorCod, mutualCod] = prestadorSeleccionado.split("-");
  try {
    const configAxios = getAxiosConfig(usuario.token, urlAxioPP);
    if (turnoSeleccionado) {
      const actualizarTurnoResponse = await axios.patch(
        `Turnos/DarTurno/${turnoSeleccionado.codigo}`,
        {
          codigo: turnoSeleccionado.codigo,
          paciente: usuario.codigo,
          mutual: mutualCod,
          prestador: prestadorCod,
          telemedicina: turnoSeleccionado.telemedicina,
          observaciones: "Turno reservado via web por el paciente",
        },
        configAxios,
      );
      return actualizarTurnoResponse;
    }
  } catch (error: any) {
    throw error;
  }
};

/**
 * Trae los horarios de atención de un prestador y mutual.
 * @param prestador String con formato "prestador-mutual".
 * @param usuario Objeto usuario (paciente).
 * @param urlAxioPP URL base de la API.
 * @returns Horarios y restricciones de atención.
 */
export const traerHorariosAtencion = async (
  prestador: string,
  usuario: any,
  urlAxioPP: string,
) => {
  let horarios = null;
  let atiende: any[] = [];
  let [prestadorCodigo, mutual] = prestador.split("-");
  const configAxios = getAxiosConfig(usuario.token, urlAxioPP);
  try {
    const [horariosResponse, atiendeResponse] = await Promise.all([
      axios.get(`PrestadoresHorarios/${prestadorCodigo}`, configAxios),
      axios.get(
        `Prestadores/RestriccionesPorMutual/${prestadorCodigo}`,
        configAxios,
      ),
    ]);
    horarios = horariosResponse.data.filter(
      (horario: any) => horario.prestador === +prestadorCodigo,
    );
    atiende = atiendeResponse.data.filter(
      (restriccion: any) =>
        restriccion.mutual === +mutual &&
        restriccion.prestador === +prestadorCodigo,
    );

    if (!horarios || !horarios[0]?.prestador) {
      throw new Error("El prestador seleccionado no tiene un horario definido");
    }

    return { horarios: horarios[0], atiende };
  } catch (e) {
    throw new Error(
      "Error al consultar los horarios disponibles del prestador",
    );
  }
};

/**
 * Obtiene todos los horarios de todos los prestadores (para uso general).
 * @param paciente Objeto paciente (debe tener token).
 * @returns Array de horarios.
 */
export const getHorariosPrestadores = async (paciente: any) => {
  let horarios = [];
  const configAxios = getAxiosConfig(paciente.token);
  try {
    const response = await axios.get(`PrestadoresHorarios`, configAxios);
    if (response.data.length !== 0) {
      horarios = response.data;
    }
  } catch (e) {
    throw new Error(
      "Error al consultar los horarios disponibles del prestador",
    );
  }
  return horarios;
};

/**
 * Trae los próximos turnos del paciente (máx 5).
 * @param paciente Objeto paciente (debe tener token y código).
 * @returns Array de turnos ordenados por fecha y hora.
 */
export const traerTurnosPaciente = async (paciente: any) => {
  let turnos = null;
  const configAxios = getAxiosConfig(paciente.token);
  try {
    const servicio = await axios
      .get(`Servicios`, configAxios)
      .then((response) => {
        if (response.data.length !== 0) {
          return response.data;
        } else {
          return null;
        }
      });
    if (servicio !== null) {
      const response = await axios.get(
        `Turnos/TurnosPaciente/?paciente=${paciente.codigo}&servicio=${servicio[0].codigo}`,
        configAxios,
      );
      if (response.data.length !== 0) {
        turnos = response.data;
        turnos = turnos.sort((a: any, b: any) => {
          const fechaA = dayjs(a.fecha);
          const fechaB = dayjs(b.fecha);

          if (fechaA.isBefore(fechaB)) {
            return -1;
          } else if (fechaA.isAfter(fechaB)) {
            return 1;
          } else {
            const horaA = dayjs(a.hora, "HH:mm:ss");
            const horaB = dayjs(b.hora, "HH:mm:ss");

            if (horaA.isBefore(horaB)) {
              return -1;
            } else if (horaA.isAfter(horaB)) {
              return 1;
            } else {
              return 0;
            }
          }
        });
        turnos = turnos.slice(0, 5);
      } else {
        turnos = [];
      }
    }
  } catch (e) {
    throw new Error("Error al consultar sus próximos turnos");
  }
  return turnos;
};
// Cancelar turno del paciente - Usa el endpoint Quitar que libera el turno
export const cancelarTurnoPaciente = async (
  token: string,
  turnoCodigo: string,
  urlAxioPP?: string,
) => {
  try {
    const configAxios = getAxiosConfig(token, urlAxioPP);
    // El endpoint Quitar/{codigo} libera el turno y lo marca como cancelado (aCancelar = false)
    // manteniendo el registro histórico para el paciente
    const response = await axios.patch(
      `Turnos/Quitar/${turnoCodigo}`,
      "",
      configAxios,
    );
    return response;
  } catch (error) {
    console.error("Error al cancelar turno del paciente:", error);
    throw error;
  }
};

// Cancelar turno del paciente (método antiguo - mantener por compatibilidad)
export const cancelarTurno = async (token: string, turnoCodigo: string) => {
  try {
    const configAxios = getAxiosConfig(token);
    const response = await axios.patch(
      `Turnos/Quitar/${turnoCodigo}`,
      "",
      configAxios,
    );
    return response;
  } catch (error) {
    console.error("Error al cancelar turno:", error);
    throw error;
  }
};

const deduplicarTurnosPorCodigo = (turnos: any[]) => {
  const turnosPorCodigo = new Map<any, any>();
  const turnosSinCodigo: any[] = [];

  turnos.forEach((turno) => {
    if (turno?.codigo === undefined || turno?.codigo === null) {
      turnosSinCodigo.push(turno);
      return;
    }

    // Si el mismo turno llega desde múltiples endpoints, conservar la última versión recibida.
    turnosPorCodigo.set(turno.codigo, turno);
  });

  return [...turnosPorCodigo.values(), ...turnosSinCodigo];
};

// Obtener turnos del paciente - Incluye activos y cancelados
export const getTurnosPaciente = async (
  token: string,
  pacienteCodigo: string,
  servicioCodigo?: string,
) => {
  try {
    const configAxios = getAxiosConfig(token);

    // Construir la URL base
    const urlBase = `Turnos/TurnosPaciente/?paciente=${pacienteCodigo}`;
    const urlConServicio = servicioCodigo
      ? `${urlBase}&servicio=${servicioCodigo}`
      : urlBase;

    // Obtener turnos activos
    const responseActivos = await axios.get(urlConServicio, configAxios);
    let turnos = responseActivos.data.length > 0 ? responseActivos.data : [];

    // Obtener también los turnos cancelados
    const responseCancelados = await axios.get(
      `${urlConServicio}&Cancelado=true`,
      configAxios,
    );
    if (responseCancelados.data.length > 0) {
      turnos = turnos.concat(responseCancelados.data);
    }

    turnos = deduplicarTurnosPorCodigo(turnos);

    // Ordenar por fecha descendente (más reciente primero)
    turnos = turnos.sort(
      (a: any, b: any) =>
        new Date(b.fecha).getTime() - new Date(a.fecha).getTime(),
    );

    return turnos;
  } catch (error) {
    console.error("Error al obtener turnos del paciente:", error);
    throw error;
  }
};
